import React, {
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import propsAreEqual from 'react-fast-compare';
import {useQuery} from '@tanstack/react-query';
import {
  ButtonWrapper,
  FilterWrapper,
  NoDataWrapper,
  QuickFilterWrapper,
  SearchBarDiv,
  StyledTable,
  ThButton
} from "@common/SortableTable.styles";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {css} from "@emotion/css";
import Helpers from "@/helpers/helpers";
import {useLocation} from 'react-router-dom';
import SortableTableRow from "@common/SortableTableRow";
import SearchBarComponent from "@common/SearchBarComponent";
import {useRosterAwareState} from "@/hooks/useRosterAwareState";
import {debounce, isEqual} from "lodash-es";
import FilterComponent, {dateRange, FilterOption, groupedValues, nonGroupedValues} from "@common/FilterComponent";
import Loading from "@common/Loading";
import Banner, {BannerTypes} from "patient-ping-remedy/packages/banner";
import DisplayHelpers from "@/helpers/display_helpers";
import QuickFilterBar, {QuickFilterInterface} from "@common/QuickFilterBar";
import FilterSelect from "@common/FilterSelect";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useRosterStore} from "@/store/roster_store";
import {FilterView, FilterResponse} from "@/api/dto/filters";
import {
  MixpanelEventType,
  MixpanelSortEvent,
  MixpanelTableSearchedEvent,
  MixpanelViewFilteredEvent,
  TableName
} from "@/api/dto/mixpanel";
import Chip from "patient-ping-remedy/packages/chip";
import {SortOrderType} from "@clients/ClientsPage";
import {useMixpanel} from "@/hooks/useMixpanel";
import {useQuickFilters} from "@/hooks/useQuickFilters";
import {useInfiniteScroll} from "@/hooks/useInfiniteScroll";
import {StyledChip} from "@common/QuickFilter.styles";
import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";

export type SortableTableHandle = {
  applyFilter: (filter: FilterResponse | object) => void;
};

export const SORT_ORDER = {
  ASC: {
    value: 'asc',
    icon: ICONS['sort-up'],
  },
  DESC: {
    value: 'desc',
    icon:  ICONS['sort-down'],
  },
  NONE: {
    value: 'none',
    icon:  ICONS.sort,
  },
};

const DATE_KEYS: Record<string, string> = {
  startDate: 'Start Date',
  endDate: 'End Date',
};

export type TableColumn = {
  tableHeader: ReactNode,
  key: string,
  type: "string" | "number" | "date" | "array" | "custom",
  value: string | number | (string | number)[],
  displayValue: React.ReactNode,
  columnWidth?: string,
};

export type TableData = {
  [key: string]: TableColumn;
};

export type CurrentSort = {
  tableColumn: TableColumn;
  sortOrder: typeof SORT_ORDER.ASC | typeof SORT_ORDER.DESC | typeof SORT_ORDER.NONE;
};

export type TableOverrides = {
  count?: number;
  searchFunction?: (searchTerm: string) => void;
  filterFunction?: (filters: object) => void;
  searchTerm?: string;
  currentFiltersOverride?: object;
  initialFilters?: object;
  quickFilters?: QuickFilterInterface[];
  activeQuickFilter?: QuickFilterInterface | null;
  sortFunction?: (sortKey: TableColumn, sortOrder: SortOrderType, tableData?: TableData[]) => TableData[] | void;
  filteredCount?: number;
  resetSortFunction?: (invalidate: boolean) => void;
  initialSort?: CurrentSort | null;
  pinnedLabel?: string;
}

type Props = PropsWithChildren & {
  id: string;
  loading: boolean;
  fetching?: boolean;
  loadError: Error | null;
  data: TableData[];
  quickFilterOptions?: QuickFilterInterface[];
  header: ReactNode[];
  searchColumns: string[];
  sortableColumns: TableColumn[];
  searchPlaceholder: string;
  filterOptions?: FilterOption[];
  pinnedFilters?: boolean;
  resetTableFilters?: boolean;
  setResetTableFilters?: (val: boolean) => void;
  ref?: React.Ref<SortableTableHandle>;
  tableOverrides?: TableOverrides;
  tabName?: TableName;
  excludeMixpanel?: boolean;
  render?: (tableData: TableData[]) => ReactNode;
  excludeFilterAndSearch?: boolean;
  columnsToResetOnSort?: TableColumn[];
  searchBy?: Value[];
  handleSearchByChange?: (value: string) => void;
  searchDropdownPlaceholder?: string;
  onNextPage?: () => void;
  noBorderRadius?: boolean;
}

const MemoizedSmallTypography = React.memo(SmallTypography);

const SortableTable = (props: Props) => {
  const { carecoApi } = useContext(CarecoApiContext);
  const currentRoster = useRosterStore((state) => state.currentRoster);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [savedFilter, setSavedFilter] = useRosterAwareState<FilterResponse | undefined>(undefined);

  const userSortedColumnRef = useRef<boolean>(false);
  const [currentSort, setCurrentSort] = useRosterAwareState<CurrentSort | null>(props.tableOverrides?.initialSort ?? null);
  const [searchTerm, setSearchTerm] = useRosterAwareState<string>('');

  const [initialFilters, setInitialFilters] = useState<object>({});
  const [currentFilters, setCurrentFilters] = useRosterAwareState<object>({});
  const [previousFilters, setPreviousFilters] = useRosterAwareState<object>({});
  const [tableData, setTableData] = useState<TableData[]>([]);

  const filteredCount = props.tableOverrides?.filteredCount ?? tableData.length;
  const allCount = props.tableOverrides?.count ?? props.data.length;
  const additionalQuickFilters = useMemo(() => {
    return props.quickFilterOptions ?? props.tableOverrides?.quickFilters ?? [];
  }, [props.quickFilterOptions, props.tableOverrides?.quickFilters]);

  const savableFilters = props.id in FilterView;
  const { data: filters } = useQuery({
    queryKey: ['filters', props.id],
    queryFn: async () => carecoApi?.getFilters(props.id),
    enabled: savableFilters && !!carecoApi && !!props.filterOptions && !props.excludeFilterAndSearch,
  });

  const {
    quickFilters,
    activeQuickFilter,
    getAllFilter,
    resetAllFilters,
  } = useQuickFilters(
    props.data,
    currentFilters,
    props.filterOptions,
    savedFilter,
    "all",
    allCount,
    additionalQuickFilters,
  );

  const { ref, listElement } = useInfiniteScroll<HTMLTableSectionElement>({
    enabled: !props.loading && !props.loadError,
    fetching: props.fetching,
    onNextPage: props.onNextPage,
  });
  const location = useLocation();
  useEffect(() => {
    setFiltersOpen(false);
    setSavedFilter(undefined);
  }, [location.pathname]);

  const searchEventSentRef = useRef(false);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  useEffect(() => {
    if (!Helpers.isEmpty(searchTerm) && !searchEventSentRef.current && !props.excludeMixpanel && mixpanelEventProperties) {
      const searchEvent: MixpanelTableSearchedEvent = {
        eventName: MixpanelEventType.BIH_TABLE_SEARCHED,
        searchBy: props.searchBy && props.searchPlaceholder,
        ...mixpanelEventProperties,
        tableName: props.tabName!,
        ...(currentRoster ? getCurrentGroupDetails(currentRoster) : {})
      };
      carecoApi?.postMixpanelEvent(searchEvent);
      searchEventSentRef.current = true;
    }

    if (Helpers.isEmpty(searchTerm)) {
      searchEventSentRef.current = false;
    }
  }, [searchTerm, props.tabName, props.excludeMixpanel, mixpanelEventProperties]);

  useEffect(() => {
    if(mixpanelEventProperties) {
      const filterKeys = Object.keys(currentFilters);
      if (filterKeys.length > 0 && !props.excludeMixpanel) {
        const filterTabName = props.tabName!;

        const event: MixpanelViewFilteredEvent = {
          eventName: MixpanelEventType.BIH_VIEW_FILTERED,
          ...mixpanelEventProperties,
          viewName: filterTabName,
          filterCriteria: filterKeys,
          ...(currentRoster ? getCurrentGroupDetails(currentRoster) : {})
        };

        carecoApi?.postMixpanelEvent(event);
      }
    }
  }, [carecoApi, currentFilters, props.tabName, props.excludeMixpanel, mixpanelEventProperties]);

  const setNewTableData = (newTableData: TableData[]) => {
    setTableData(newTableData);
  };

  useEffect(() => {
    setNewTableData(props.data);

    if(!Helpers.isEmptyObject(currentFilters)) {
      filterFunction(currentFilters);
    } else if(props.tableOverrides?.currentFiltersOverride) {
      setCurrentFilters(props.tableOverrides.currentFiltersOverride);
      filterFunction(props.tableOverrides.currentFiltersOverride);
    } else if(props.tableOverrides?.initialFilters) {
      // filter table again only if initial filters have changed
      if(!isEqual(initialFilters, props.tableOverrides.initialFilters)) {
        setInitialFilters(props.tableOverrides.initialFilters);
        setCurrentFilters(props.tableOverrides.initialFilters);
        filterFunction(props.tableOverrides.initialFilters, true);
      }
    }
  }, [props.data, props.filterOptions, props.quickFilterOptions, props.tableOverrides?.currentFiltersOverride]);

  const selectTableFilters = (open: boolean, savedFilter?: FilterResponse) => {
    setFiltersOpen(open);
    setSavedFilter(savedFilter);
    const currentFilters = savedFilter ? Helpers.safeJsonParse(savedFilter.data, initialFilters) : initialFilters;
    setCurrentFilters(currentFilters);
    filter(currentFilters);
  };

  useEffect(() => {
    const defaultFilter = filters?.find((filter) => filter.isDefault);
    if (savedFilter) {
      const filter = filters?.find((filter) => filter.id === savedFilter.id);
      selectTableFilters(false, filter ?? defaultFilter);
    } else if (defaultFilter) {
      selectTableFilters(false, defaultFilter);
    }
  }, [currentRoster, filters]);

  const resetTableFilters = () => {
    setSavedFilter(undefined);
    setCurrentSort(null);
    setSearchTerm('');
    setCurrentFilters({});
    setPreviousFilters({});
  };

  useEffect(() => {
    if(props.resetTableFilters) {
      resetTableFilters();
      props.setResetTableFilters!(false);
    }
  }, [props.resetTableFilters, props.setResetTableFilters]);

  useImperativeHandle(props.ref, () => ({
    applyFilter(externalFilter: FilterResponse | object) {
      if ('id' in externalFilter) {
        selectTableFilters(false, externalFilter as FilterResponse);
      } else {
        setSavedFilter(undefined);
        setCurrentFilters(externalFilter);
        filter(externalFilter);
      }
    }
  }));

  const isResettableSort = (currentSort: CurrentSort | null) => {
    return (currentSort && props.columnsToResetOnSort
      ?.some(col => col.key === currentSort.tableColumn.key)) ?? false;
  };

  const sortBy = (newCurrentSort: CurrentSort) => {
    const isResettable = isResettableSort(newCurrentSort);
    if(isResettable) {
      setSavedFilter(undefined);
      setCurrentFilters({});
      setPreviousFilters({});
    }
    if(props?.tableOverrides?.sortFunction) {
      const sortedTableData = props.tableOverrides.sortFunction(
        newCurrentSort.tableColumn,
        newCurrentSort.sortOrder,
        isResettable ? props.data : tableData);
      if(sortedTableData) {
        setTableData(sortedTableData);
      }
    } else {
      const sortedTableData = Helpers.sort(isResettable ? props.data : tableData,
        newCurrentSort.tableColumn.type,
        newCurrentSort.tableColumn.key,
        newCurrentSort.sortOrder.value);
      setTableData(sortedTableData);
    }
  };

  const search = (searchTerm: string) => {
    setSavedFilter(undefined);
    setSearchTerm(searchTerm);
    setCurrentFilters({});
    setPreviousFilters({});
    (props.tableOverrides?.searchFunction ?? searchFunction)(searchTerm);
  };

  const searchFunction = debounce((searchTerm: string) => {
    if(!Helpers.isEmpty(searchTerm)) {
      const searchedTableData = Helpers.search(searchTerm, props.data, props.searchColumns);
      setNewTableData(searchedTableData);
    } else {
      setNewTableData(props.data);
    }
  }, 500);

  const filter = (filters: object) => {
    setCurrentSort(null);
    setSearchTerm('');
    if (props.tableOverrides?.resetSortFunction) {
      props.tableOverrides.resetSortFunction(isResettableSort(currentSort));
    }
    (props.tableOverrides?.filterFunction ?? filterFunction)(filters);
  };

  const filterBy = (value: nonGroupedValues[], key: string) => {
    filter(getSetNewCurrentFilters(value, key));
  };

  const getSetNewCurrentFilters = (value: nonGroupedValues[] | dateRange, key: string) => {
    const newFilters: Record<string, unknown> = { ...currentFilters, [key]: value };
    if(Array.isArray(value)) {
      if(Helpers.isEmptyArray(value)) {
        delete newFilters[key as keyof typeof newFilters];
      } else {
        newFilters[key as keyof typeof newFilters] = value;
      }
    } else {
      const dateRangeValue = value as dateRange;
      if (!dateRangeValue.startDate && !dateRangeValue.endDate) {
        delete newFilters[key as keyof typeof newFilters];
      } else {
        newFilters[key as keyof typeof newFilters] = dateRangeValue;
      }
    }

    setCurrentFilters(newFilters);
    return newFilters;
  };

  const filterFunction = (filters: object, ignoreChange?: boolean) => {
    setNewTableData(Helpers.filter(filters, ignoreChange ? filters : previousFilters, props.data, props.filterOptions ?? []));
    setPreviousFilters(filters);
  };

  const quickFilterFunction = (quickFilter: QuickFilterInterface) => {
    if(quickFilter.key === 'all') {
      filterFunction({});
      resetTableFilters();
    } else {
      filterBy([quickFilter.value!], quickFilter.key);
    }
    setSearchTerm('');
    if(props.tableOverrides?.searchFunction) {
      props.tableOverrides.searchFunction!('');
    }
  };

  useEffect(() => {
    if (userSortedColumnRef.current &&currentSort && props.tabName && !props.excludeMixpanel && mixpanelEventProperties) {
      const mixpanelEvent: MixpanelSortEvent = {
        eventName: MixpanelEventType.BIH_TABLE_SORTED,
        ...mixpanelEventProperties,
        tableName: props.tabName!,
        sortCriteria: currentSort.tableColumn.tableHeader?.toString(),
        ...(currentRoster ? getCurrentGroupDetails(currentRoster) : {})
      };
      carecoApi?.postMixpanelEvent(mixpanelEvent);
    }
  }, [currentSort, props.tabName, props.excludeMixpanel, mixpanelEventProperties]);

  const getHeaders = (header: ReactNode[] | string[], sortColumns: TableColumn[]): React.ReactNode => {
    return (
      <tr>
        {header.map((item, index) => {
          const sortable = sortColumns.find((column: TableColumn) => column.tableHeader === item);
          const isCurrentSort = currentSort && currentSort?.tableColumn.tableHeader === item;
          const sortOrder = isCurrentSort ? currentSort!.sortOrder : SORT_ORDER.NONE;
          const newCurrentSort = {
            tableColumn: sortable!,
            sortOrder: sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC
          };

          if(sortable) {
            return (
              <ThButton key={index}
                onClick={() => {
                  userSortedColumnRef.current = true;
                  setCurrentSort(newCurrentSort);
                  sortBy(newCurrentSort);
                }}>
                <MemoizedSmallTypography>
                  {item}
                  {
                    currentSort && isCurrentSort &&
                    <Icon iconClass={currentSort.sortOrder.icon}
                      className={css({ marginLeft: '5px', fontSize: '14px'})} />
                  }

                  {
                    (!currentSort || !isCurrentSort) &&
                    <Icon iconClass={ICONS.sort}
                      className={css({ marginLeft: '5px', fontSize: '14px'})} />
                  }
                </MemoizedSmallTypography>
              </ThButton>
            );
          } else {
            return (
              <th key={index}>
                <MemoizedSmallTypography>{item}</MemoizedSmallTypography>
              </th>
            );
          }
        })}
      </tr>
    );
  };

  const getBody = (data: TableData[]): React.ReactNode => {
    return (
      <>
        {data.map((row: TableData, index) => (
          <SortableTableRow row={row} key={index} />
        ))}
      </>
    );
  };

  const immutable = !filtersOpen && !!savedFilter;

  const filtersApplied =  Object.values(currentFilters)?.reduce((accumulator, currentValue) => {
    if (Array.isArray(currentValue)) {
      return accumulator + currentValue.length;
    }
    if (currentValue && typeof currentValue === 'object') {
      return accumulator + (Object.values(currentValue).filter(Boolean).length);
    }
    return accumulator + 1;
  }, activeQuickFilter ? -1 : 0);

  const getAppliedFilters = () => {
    const Component = immutable ? StyledChip : Chip;

    if (filtersApplied + quickFilters.length - 1 > 6) {
      const allFilter = getAllFilter();

      return <Component disabled={immutable} negated={immutable} onClick={() =>
        resetAllFilters(allFilter, () => quickFilterFunction(allFilter))
      }>
        {filtersApplied} filters applied
      </Component>;
    }

    return Object.entries(currentFilters).map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .filter((filterValue: nonGroupedValues | groupedValues) => !filterValue.quickFilter)
          .map((filterValue: nonGroupedValues | groupedValues, index: number) => {
            const isGroupedValue = filterValue && typeof filterValue === 'object' && 'group' in filterValue;
            const label = isGroupedValue ? `${(filterValue as groupedValues).group} - ${filterValue.label}` : filterValue.label;

            return <Component key={`${key}-${index}`} disabled={immutable} negated={immutable} onClick={() => filterBy(
              value.filter((option: nonGroupedValues | groupedValues) => option.id !== filterValue.id),
              key
            )}>
              {label}
            </Component>;
          }
          );
      } else {
        return Object.entries(value).map(([subKey, subValue], index) => {
          if (subValue) {
            const isDate = subKey in DATE_KEYS;
            const displayLabel = isDate ? DATE_KEYS[subKey] : DisplayHelpers.humanize(subKey);
            const displayValue = isDate ? DisplayHelpers.formatDate(subValue.toString()) : subValue.toString();

            return <Component key={`${key}-${index}`} disabled={immutable} negated={immutable} onClick={() => {
              delete value[subKey];
              filterBy(value, key);
            }}>
              {displayLabel}: {displayValue}
            </Component>;
          }

          return null;
        });
      }
    });
  };

  return (
    <>
      {!props.excludeFilterAndSearch && (
        <>
          <SearchBarDiv>
            {props.filterOptions && (
              <>
                {savableFilters && (
                  <FilterSelect
                    id={props.id}
                    filters={filters ?? []}
                    savedFilter={savedFilter}
                    onChange={selectTableFilters}
                  />
                )}
                <FilterComponent
                  isOpen={filtersOpen}
                  handleOpen={setFiltersOpen}
                  id={props.id}
                  filterFunction={filterBy}
                  filterArgs={{
                    filterOptions: props.filterOptions,
                    currentFilters: props.tableOverrides?.currentFiltersOverride ?? currentFilters,
                  }}
                  savedFilter={savedFilter}
                  savableFilters={savableFilters}
                  pinnedFilters={props.pinnedFilters}
                  pinnedCount={filters?.filter((f) => f.isPinned).length}
                  pinnedLabel={props.tableOverrides?.pinnedLabel}
                  initialFilters={props.tableOverrides?.initialFilters as Record<string, unknown>}
                  onSave={setSavedFilter}
                  onReset={(filters) => {
                    setCurrentFilters(filters);
                    filter(filters);
                  }}
                />
              </>
            )}
            <SearchBarComponent
              id={props.id}
              searchIcon={true}
              searchTerm={props.tableOverrides?.searchTerm ?? searchTerm}
              searchFunction={search}
              searchPlaceholder={props.searchPlaceholder}
              searchBy={props?.searchBy}
              handleSearchByChange={props.handleSearchByChange}
              searchDropdownPlaceholder={props.searchDropdownPlaceholder}
            />
          </SearchBarDiv>
          <FilterWrapper>
            <QuickFilterWrapper>
              <QuickFilterBar
                quickFilters={quickFilters}
                activeFilter={activeQuickFilter}
                filterFunction={quickFilterFunction}
              />
              {filtersApplied > 0 && (
                <StyledChip disabled={true} negated={immutable}>
                  Filtered Results: {filteredCount}
                </StyledChip>
              )}
              {getAppliedFilters()}
            </QuickFilterWrapper>
            <ButtonWrapper>
              {props.children}
            </ButtonWrapper>
          </FilterWrapper>
        </>
      )}
      {props.loading && <Loading />}
      {props.loadError && <Banner type={BannerTypes.WARNING}>{DisplayHelpers.displayErrorMessage(props.loadError)}</Banner>}
      {!props.loading && !props.loadError && (
        props.render
          ? props.render(tableData)
          : <StyledTable id={props.id} noBorderRadius={props.noBorderRadius}>
            <thead>{getHeaders(props.header, props.sortableColumns)}</thead>
            <tbody ref={listElement}>{getBody(tableData)}</tbody>
          </StyledTable>
      )}
      {!props.loading && !props.loadError && tableData.length === 0 && (
        props.render
          ? <NoDataWrapper>No data to display</NoDataWrapper>
          : <StyledTable>
            <tbody>
              <tr><td><NoDataWrapper>No data to display</NoDataWrapper></td></tr>
            </tbody>
          </StyledTable>
      )}
      <span ref={ref} />
      {!props.loading && props.fetching && <Loading />}
    </>
  );
};

export default React.memo(SortableTable, propsAreEqual);
